import {
  Avatar,
  Button,
  Card,
  CardCover,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Input,
  Option,
  Select,
  Sheet,
  Stack,
  Typography,
} from "@mui/joy";
import PageTitleBox from "../../../components/PageTitleBox";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import LoadingModal from "../../../components/LoadingModal/LoadingModal";
import { theme } from "../../../components/Theme";
import { BuildingOffice2Icon, TruckIcon } from "@heroicons/react/24/outline";
import { PolicyBullet } from "../../../components/PasswordPolicy/PolicyBullet";
import useContainer from "./useContainer";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthStore } from "../../../store/session";
import { KeyboardArrowDown } from "@mui/icons-material";
import FlagById from "../../../components/FlagById";
import { animateSelectStyle } from "../../../styles/select";
import { countries } from "../../../components/Countries";
import {
  CountryCode,
  PhNoFormat,
  PhNoWhiteListedCountries,
  WhiteListedCountries,
} from "../../../utils/constants";
import PhoneInput from "react-phone-input-2";
import { BusinessSessionHelper } from "../../../helpers/business-session.helper";
import startsWith from "lodash.startswith";
import { matchesFormat } from "../../../utils/misc";
import isEqual from "lodash.isequal";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import debounce from "lodash.debounce";
import NotFound from "../../NotFound/NotFound";

const UpdatePersonalProfile = () => {
  const { t } = useTranslation();

  const { businessId } = useParams();

  const { session } = useAuthStore();

  const navigate = useNavigate();

  const [isAddressMatch, setIsAddressMatch] = useState(true);

  const {
    isLoading,
    setIsLoading,
    profile,
    displayPicture,
    setDisplayPicture,
    address,
    business,
    companyIcon,
    setUploadedProfilePicture,
    setUploadedCompanyPicture,
    generalSchema,
    setCompanyIcon,
    getProfile,
    getDefaultAddress,
    getBusiness,
    handleMapAddressToCompanyAddress,
    handleCompareAddresses,
    updateProfileMutation,
  } = useContainer();

  const methods = useForm<z.infer<typeof generalSchema>>({
    resolver: zodResolver(generalSchema),
    mode: "onChange",
  });

  const {
    control,
    register,
    formState: { errors },
    reset,
    setValue,
    watch,
    setError,
    trigger,
    clearErrors,
    getValues,
    handleSubmit,
  } = methods;

  // watched items
  const watchedAddress = useWatch({
    control,
    name: "address",
  });
  const watchedCompanyAddress = useWatch({
    control,
    name: [
      "companyCountry",
      "companyNumber",
      "companyNumberCountryCode",
      "companyAddress",
      "companyAddress2",
      "companyProvince",
      "companyPostalCode",
    ],
    defaultValue: {
      companyAddress: "",
      companyAddress2: "",
      companyProvince: "",
      companyPostalCode: "",
    },
  });

  const debouncedSyncAddress = useCallback(
    debounce(() => {
      const addressFields = getValues("address");

      setValue("companyCountry", addressFields?.countryCode);
      setValue("companyNumber", addressFields?.phoneNumber);
      setValue(
        "companyNumberCountryCode",
        addressFields?.phoneNumberCountryCode
      );
      setValue("companyProvince", addressFields?.province);
      setValue("companyAddress", addressFields?.address);
      setValue("companyAddress2", addressFields?.address2);
      setValue("companyPostalCode", addressFields?.postalCode);
    }, 500),
    [getValues, setValue]
  );

  /**
   * fetch required data (profile, address, business)
   * and set form values
   */
  useEffect(() => {
    if (!businessId) {
      return;
    }

    if (!profile) getProfile();
    if (!address) getDefaultAddress();
    if (!business) getBusiness(businessId);

    const checkAddressMatch = handleCompareAddresses(business, address);
    setIsAddressMatch(checkAddressMatch);

    const companyAddressFields = checkAddressMatch
      ? handleMapAddressToCompanyAddress(business, address)
      : {};

    // mapping fetched data to set defaultValues
    reset({
      ...profile,
      mobile: `+${profile?.countryCode} ${profile?.localNumber}`,
      email: session?.account.email || "",
      address: {
        ...address,
        address: address?.address,
        address2: address?.address2,
      },
      ...business,
      companyCountry: business?.companyCountry || address?.countryCode,
      companyNumber: business?.companyNumber || address?.phoneNumber,
      ...companyAddressFields,
    });
  }, [profile, address, business]);

  /**
   * set companyAddress fields with address values
   */
  useEffect(() => {
    if (!isAddressMatch) return;

    debouncedSyncAddress();

    return () => {
      debouncedSyncAddress.cancel();
    };
  }, [watchedAddress, isAddressMatch]);

  /**
   * check if watchedCompanyAddress is different
   * than address, then setIsAddressMatch to false
   */
  useEffect(() => {
    const addressFields = getValues([
      "address.countryCode",
      "address.phoneNumber",
      "address.phoneNumberCountryCode",
      "address.address",
      "address.address2",
      "address.province",
      "address.postalCode",
    ]);

    console.log("WHEN DID IT DE SYNC?", {
      addressFields,
      watchedCompanyAddress,
      isequal: isEqual(watchedCompanyAddress, addressFields),
    });

    if (!isEqual(watchedCompanyAddress, addressFields))
      setIsAddressMatch(false);
    else setIsAddressMatch(true);
  }, [watchedCompanyAddress]);

  /**
   * Handles file change event from profile picture input.
   * It will update the display picture URL.
   * @param {any} e the event object
   */
  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    handler: (file: File) => void
  ) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      handler(file);
    }
  };

  if (!businessId || business?.businessAccountType !== "BUYER") {
    return <NotFound />;
  }

  const onSubmit = async (data: any) => {
    console.log("SUBMIT DATA", { data, session });
    if (!profile || !business) return;

    setIsLoading(true);

    const payload = {
      ...data,
      personalAddress: data.address,
    };

    updateProfileMutation.mutate({
      payload,
      id: profile.id,
    });
  };

  return (
    <Sheet
      sx={{
        backgroundColor: "transparent",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
        gap: "32px",
        py: "32px",
      }}
    >
      <Stack direction={"row"} display={{ xs: "none", md: "flex" }}>
        <PageTitleBox title={t("breadcrumb.editProfile")} />
      </Stack>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack
          direction={"column"}
          sx={{
            gap: "32px",
          }}
        >
          {/* account info */}
          <Stack
            direction={"column"}
            sx={{
              alignItems: "start",
              justifyContent: "start",
              p: "24px",
              borderColor: theme.palette.neutral[300],
              borderRadius: "8px",
              borderWidth: "1px",
              gap: "16px",
            }}
          >
            <Typography
              level="title-md"
              startDecorator={<BuildingOffice2Icon width={20} />}
            >
              {t("profile.editProfile.accountInfo.title")}
            </Typography>

            {/* change display picture */}
            <Stack
              direction={{ xs: "column", md: "row" }}
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                gap: "8px",
              }}
            >
              <Stack
                direction={"row"}
                sx={{
                  gap: "8px",
                  alignItems: "center",
                }}
              >
                <Avatar
                  alt="Linkz User"
                  size="lg"
                  src={displayPicture ?? ""}
                  sx={{ height: 80, width: 80 }}
                />

                <CustomImagePolicy file={null} />
              </Stack>

              <Button
                variant="linkz-outlined-primary"
                sx={{ mr: { xs: "auto", md: 0 }, ml: { xs: 0, md: "auto" } }}
                onClick={() =>
                  document.getElementById("display-picture")?.click()
                }
              >
                {t("commons.uploadBtn")}
              </Button>
              <input
                id="display-picture"
                accept="image/*"
                type="file"
                style={{ display: "none" }}
                onChange={(e) =>
                  handleFileChange(e, (file) => {
                    setUploadedProfilePicture(file);
                    setDisplayPicture(URL.createObjectURL(file));
                  })
                }
              />
            </Stack>

            {/* account details */}
            <Grid
              container
              spacing={2}
              sx={{
                width: "100%",
              }}
            >
              {/* fullName */}
              <Grid xs={12}>
                <FormControl error={!!errors.fullName}>
                  <FormLabel>
                    {t("profile.editProfile.accountInfo.fullName")}
                    <span className="text-danger-500">*</span>
                  </FormLabel>
                  <Input {...register("fullName", { required: true })} />
                  {errors.fullName?.message && (
                    <FormHelperText>{errors.fullName?.message}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* phoneNumber */}
              <Grid xs={12} md={9}>
                <FormControl error={!!errors.mobile}>
                  <FormLabel>
                    {t("profile.editProfile.accountInfo.mobile")}
                  </FormLabel>
                  <Input disabled {...register("mobile")} />
                  {errors.mobile?.message && (
                    <FormHelperText>{errors.mobile?.message}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid
                xs={12}
                md={3}
                sx={{
                  display: "flex",
                  alignItems: "flex-end",
                }}
              >
                <Button
                  variant="linkz-outlined-primary"
                  onClick={() => navigate("/client/profile/mobile")}
                  sx={{
                    width: { xs: "70%", md: "100%" },
                  }}
                >
                  {t("profile.editProfile.accountInfo.changeMobileBtn")}
                </Button>
              </Grid>

              {/* email */}
              <Grid xs={12}>
                <FormControl error={!!errors.email}>
                  <FormLabel>
                    {t("profile.editProfile.accountInfo.email")}
                  </FormLabel>
                  <Input
                    disabled={
                      session?.account.authStrategy === "EMAIL_AND_PASSWORD"
                    }
                    {...register("email")}
                  />
                  {errors.email?.message && (
                    <FormHelperText>{errors.email?.message}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </Stack>

          {/* personal address */}
          <Stack
            direction={"column"}
            sx={{
              alignItems: "start",
              justifyContent: "start",
              p: "24px",
              borderColor: theme.palette.neutral[300],
              borderRadius: "8px",
              borderWidth: "1px",
              gap: "16px",
            }}
          >
            <Stack
              direction={"row"}
              sx={{
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Typography
                level="title-md"
                startDecorator={<BuildingOffice2Icon width={20} />}
              >
                {t("profile.editProfile.personalAddress.title")}
              </Typography>

              {/* defautl label */}
              <Chip variant="outlined" color="primary">
                Default
              </Chip>
            </Stack>

            {/* personal address */}
            <Grid
              container
              spacing={2}
              sx={{
                width: "100%",
              }}
            >
              {/* addressTitle */}
              <Grid xs={12}>
                <FormControl error={!!errors.address?.title}>
                  <FormLabel>
                    {t("profile.editProfile.personalAddress.addressTitle")}
                    <span className="text-danger-500">*</span>
                  </FormLabel>
                  <Input {...register("address.title", { required: true })} />
                  {errors.address?.title?.message && (
                    <FormHelperText>
                      {errors.address?.title?.message}
                    </FormHelperText>
                  )}
                  <Typography
                    level="text-xs-regular"
                    textColor={theme.palette.neutral[600]}
                    mt={"4px"}
                  >
                    {t(
                      "profile.editProfile.personalAddress.addressTitlePrompt"
                    )}
                  </Typography>
                </FormControl>
              </Grid>

              {/* addressFullName */}
              <Grid xs={12}>
                <FormControl error={!!errors.address?.fullName}>
                  <FormLabel>
                    {t("profile.address.fullName")}
                    <span className="text-danger-500">*</span>
                  </FormLabel>
                  <Input
                    {...register("address.fullName", { required: true })}
                  />
                  {errors.address?.fullName?.message && (
                    <FormHelperText>
                      {errors.address?.fullName?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* addressCountry & addressPhoneNumber */}
              <Grid xs={12} md={6}>
                <FormControl error={!!errors.address?.countryCode}>
                  <FormLabel>{t("profile.address.country")}</FormLabel>
                  <Select
                    placeholder={t("profile.address.country")}
                    indicator={<KeyboardArrowDown />}
                    onChange={(_, value) => {
                      if (value) setValue("address.countryCode", value);
                    }}
                    renderValue={(option) => (
                      <Typography
                        startDecorator={
                          <FlagById id={`${option?.value ?? ""}`} />
                        }
                      >
                        {option?.label}
                      </Typography>
                    )}
                    value={watch("address.countryCode")}
                    sx={{ ...animateSelectStyle, backgroundColor: "white" }}
                  >
                    {countries
                      .filter((country) =>
                        WhiteListedCountries.includes(country.code)
                      )
                      .map((country) => (
                        <Option
                          key={country.code}
                          value={country.code}
                          label={country.label}
                        >
                          <Typography
                            startDecorator={<FlagById id={country.code} />}
                          >
                            {country.label}
                          </Typography>
                        </Option>
                      ))}
                  </Select>
                  {errors.address?.countryCode?.message && (
                    <FormHelperText>
                      {errors.address?.countryCode?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid xs={12} md={6}>
                <FormControl error={!!errors.address?.phoneNumber}>
                  <FormLabel>
                    {t("profile.address.phoneNumber")}
                    <span className="text-danger-500">*</span>
                  </FormLabel>
                  <PhoneInput
                    value={
                      (watch("address.phoneNumber") &&
                        `+${watch("address.phoneNumberCountryCode")}${watch(
                          "address.phoneNumber"
                        )}`) ||
                      ""
                    }
                    onlyCountries={PhNoWhiteListedCountries}
                    enableTerritories={false}
                    country={BusinessSessionHelper.GetBusinessCountryID().toLocaleLowerCase()}
                    masks={{
                      my: "..-....-....",
                      sg: "....-....",
                      th: "... ... ...",
                      id: "... ... ... ...",
                      mm: ". .... .... .....",
                      au: "... ... ...",
                    }}
                    isValid={(inputNumber, meta: any, countries) => {
                      if (inputNumber === "" || inputNumber === meta.dialCode) {
                        return true;
                      }

                      if (
                        inputNumber.toString().startsWith(`${meta.dialCode}0`)
                      ) {
                        if (!errors.address?.phoneNumber?.message) {
                          setError("address.phoneNumber", {
                            message: t(
                              "commons.error.form.mobileInvalidStartZero"
                            ),
                          });
                        }
                        return false;
                      }

                      const result = (countries || []).some((country: any) => {
                        if (
                          startsWith(inputNumber, country.dialCode) ||
                          startsWith(country.dialCode, inputNumber)
                        ) {
                          const iso2: CountryCode = meta.iso2 as CountryCode;
                          const format = PhNoFormat[iso2];
                          if (!format) return false;
                          return matchesFormat(
                            inputNumber,
                            format,
                            country.countryCode
                          );
                        }
                        return false;
                      });

                      if (!result && !errors.address?.phoneNumber?.message) {
                        setError("address.phoneNumber", {
                          message: t("commons.error.form.mobileInvalid"),
                        });
                      } else if (
                        result &&
                        !!errors.address?.phoneNumber?.message
                      ) {
                        if (errors.address?.phoneNumber.type === "manual") {
                          return false;
                        }
                        clearErrors("address.phoneNumber");
                        trigger("address.phoneNumber");
                      }
                      return result;
                    }}
                    inputStyle={{
                      width: "100%",
                      fontSize: "16px",
                      minHeight: "2.8rem",
                      height: "auto",
                      borderRadius: "8px",
                      fontWeight: 500,
                    }}
                    buttonStyle={{
                      borderRadius: "8px 0 0 8px",
                    }}
                    onChange={(phone, meta: any) => {
                      setValue("address.phoneNumberCountryCode", meta.dialCode);
                      setValue(
                        "address.phoneNumber",
                        phone.replace(new RegExp(meta.dialCode), "")
                      );
                    }}
                  />
                  {errors.address?.phoneNumber?.message && (
                    <FormHelperText>
                      {errors.address?.phoneNumber?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* addressLine1 */}
              <Grid xs={12}>
                <FormControl error={!!errors.address?.address}>
                  <FormLabel>
                    {t("profile.editProfile.personalAddress.addressLine1")}
                    <span className="text-danger-500">*</span>
                  </FormLabel>
                  <Input {...register("address.address", { required: true })} />
                  {errors.address?.address?.message && (
                    <FormHelperText>
                      {errors.address?.address?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* addressLine2 */}
              <Grid xs={12}>
                <FormControl error={!!errors.address?.address2}>
                  <FormLabel>
                    {t("profile.editProfile.personalAddress.addressLine2")}
                  </FormLabel>
                  <Input {...register("address.address2")} />
                  {errors.address?.address2?.message && (
                    <FormHelperText>
                      {errors.address?.address2?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* addressProvince & addressPostalCode */}
              <Grid xs={12} md={6}>
                <FormControl error={!!errors.address?.province}>
                  <FormLabel>
                    {t("profile.address.provinceState")}
                    <span className="text-danger-500">*</span>
                  </FormLabel>
                  <Input
                    placeholder={t("profile.address.typeProvinceState")}
                    {...register("address.province")}
                  />
                  {errors.address?.province?.message && (
                    <FormHelperText>
                      {errors.address?.province?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid xs={12} md={6}>
                <FormControl error={!!errors.address?.postalCode}>
                  <FormLabel>{t("profile.address.postalCode")}</FormLabel>
                  <Input
                    placeholder={t("profile.address.typePostalCode")}
                    {...register("address.postalCode")}
                  />
                  {errors.address?.postalCode?.message && (
                    <FormHelperText>
                      {errors.address?.postalCode?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </Stack>

          {/* company info */}
          <Stack
            direction={"column"}
            sx={{
              alignItems: "start",
              justifyContent: "start",
              p: "24px",
              borderColor: theme.palette.neutral[300],
              borderRadius: "8px",
              borderWidth: "1px",
              gap: "16px",
            }}
          >
            <Typography
              level="title-md"
              startDecorator={<BuildingOffice2Icon width={20} />}
            >
              {t("profile.editProfile.companyInfo.title")}
            </Typography>

            {/* change company picture */}
            <Stack
              direction={{ xs: "column", md: "row" }}
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                gap: "8px",
              }}
            >
              <Stack
                direction={"row"}
                sx={{
                  gap: "8px",
                  alignItems: "center",
                }}
              >
                <Card
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: "8px",
                  }}
                >
                  <CardCover>
                    <img
                      id="companyIcon"
                      src={
                        companyIcon
                          ? URL.createObjectURL(companyIcon)
                          : "/upload-placeholder.png"
                      }
                    />
                  </CardCover>
                </Card>

                <CustomImagePolicy file={companyIcon} />
              </Stack>

              <Button
                variant="linkz-outlined-primary"
                sx={{ mr: { xs: "auto", md: 0 }, ml: { xs: 0, md: "auto" } }}
                onClick={() =>
                  document.getElementById("display-picture-company")?.click()
                }
              >
                {t("commons.uploadBtn")}
              </Button>
              <input
                id="display-picture-company"
                accept="image/*"
                type="file"
                style={{ display: "none" }}
                onChange={(e) =>
                  handleFileChange(e, (file) => {
                    setUploadedCompanyPicture(file);
                    setCompanyIcon(file);
                  })
                }
              />
            </Stack>

            {/* company details */}
            <Grid
              container
              spacing={2}
              sx={{
                width: "100%",
              }}
            >
              {/* companyName */}
              <Grid xs={12}>
                <FormControl error={!!errors.companyName}>
                  <FormLabel>
                    {t("profile.editProfile.companyInfo.companyName")}
                    <span className="text-danger-500">*</span>
                  </FormLabel>
                  <Input {...register("companyName", { required: true })} />
                  {errors.companyName?.message && (
                    <FormHelperText>
                      {errors.companyName?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* companyWebsite */}
              <Grid xs={12}>
                <FormControl error={!!errors.companyWebsite}>
                  <FormLabel>
                    {t("profile.editProfile.companyInfo.companyWebsite")}
                  </FormLabel>
                  <Input {...register("companyWebsite")} />
                  {errors.companyWebsite?.message && (
                    <FormHelperText>
                      {errors.companyWebsite?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </Stack>

          {/* company address */}
          <Stack
            direction={"column"}
            sx={{
              alignItems: "start",
              justifyContent: "start",
              p: "24px",
              borderColor: theme.palette.neutral[300],
              borderRadius: "8px",
              borderWidth: "1px",
              gap: "16px",
            }}
          >
            <Stack
              direction={"row"}
              sx={{
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Typography
                level="title-md"
                startDecorator={<TruckIcon width={20} />}
              >
                {t("profile.editProfile.companyAddress.title")}
              </Typography>
            </Stack>

            {/* company address */}
            <Grid
              container
              spacing={2}
              sx={{
                width: "100%",
              }}
            >
              {/* companyCountry & companyPhoneNumber */}
              <Grid xs={12} md={6}>
                <FormControl error={!!errors.companyCountry}>
                  <FormLabel>{t("profile.address.country")}</FormLabel>
                  <Select
                    placeholder={t("profile.address.country")}
                    indicator={<KeyboardArrowDown />}
                    onChange={(_, value) => {
                      if (value) setValue("companyCountry", value);

                      // set isAddressMatch to false if value changed
                      if (value !== getValues("address.countryCode"))
                        setIsAddressMatch(false);
                    }}
                    renderValue={(option) => (
                      <Typography
                        startDecorator={
                          <FlagById id={`${option?.value ?? ""}`} />
                        }
                      >
                        {option?.label}
                      </Typography>
                    )}
                    value={
                      isAddressMatch
                        ? getValues("address.countryCode")
                        : watch("companyCountry")
                    }
                    sx={{ ...animateSelectStyle, backgroundColor: "white" }}
                  >
                    {countries
                      .filter((country) =>
                        WhiteListedCountries.includes(country.code)
                      )
                      .map((country) => (
                        <Option
                          key={country.code}
                          value={country.code}
                          label={country.label}
                        >
                          <Typography
                            startDecorator={<FlagById id={country.code} />}
                          >
                            {country.label}
                          </Typography>
                        </Option>
                      ))}
                  </Select>
                  {errors.companyCountry?.message && (
                    <FormHelperText>
                      {errors.companyCountry?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid xs={12} md={6}>
                <FormControl error={!!errors.companyNumber}>
                  <FormLabel>
                    {t("profile.address.phoneNumber")}
                    <span className="text-danger-500">*</span>
                  </FormLabel>
                  <PhoneInput
                    value={
                      (watch("companyNumber") &&
                        `+${watch("companyNumberCountryCode")}${watch(
                          "companyNumber"
                        )}`) ||
                      ""
                    }
                    onlyCountries={PhNoWhiteListedCountries}
                    enableTerritories={false}
                    country={BusinessSessionHelper.GetBusinessCountryID().toLocaleLowerCase()}
                    masks={{
                      my: "..-....-....",
                      sg: "....-....",
                      th: "... ... ...",
                      id: "... ... ... ...",
                      mm: ". .... .... .....",
                      au: "... ... ...",
                    }}
                    isValid={(inputNumber, meta: any, countries) => {
                      if (inputNumber === "" || inputNumber === meta.dialCode) {
                        return true;
                      }

                      if (
                        inputNumber.toString().startsWith(`${meta.dialCode}0`)
                      ) {
                        if (!errors.companyNumber?.message) {
                          setError("companyNumber", {
                            message: t(
                              "commons.error.form.mobileInvalidStartZero"
                            ),
                          });
                        }
                        return false;
                      }

                      const result = (countries || []).some((country: any) => {
                        if (
                          startsWith(inputNumber, country.dialCode) ||
                          startsWith(country.dialCode, inputNumber)
                        ) {
                          const iso2: CountryCode = meta.iso2 as CountryCode;
                          const format = PhNoFormat[iso2];
                          if (!format) return false;
                          return matchesFormat(
                            inputNumber,
                            format,
                            country.countryCode
                          );
                        }
                        return false;
                      });

                      if (!result && !errors.companyNumber?.message) {
                        setError("companyNumber", {
                          message: t("commons.error.form.mobileInvalid"),
                        });
                      } else if (result && !!errors.companyNumber?.message) {
                        if (errors.companyNumber.type === "manual") {
                          return false;
                        }
                        clearErrors("companyNumber");
                        trigger("companyNumber");
                      }
                      return result;
                    }}
                    inputStyle={{
                      width: "100%",
                      fontSize: "16px",
                      minHeight: "2.8rem",
                      height: "auto",
                      borderRadius: "8px",
                      fontWeight: 500,
                    }}
                    buttonStyle={{
                      borderRadius: "8px 0 0 8px",
                    }}
                    onChange={(phone, meta: any) => {
                      setValue("companyNumberCountryCode", meta.dialCode);
                      setValue(
                        "companyNumber",
                        phone.replace(new RegExp(meta.dialCode), "")
                      );
                    }}
                  />
                  {errors.companyNumber?.message && (
                    <FormHelperText>
                      {errors.companyNumber?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* companyAddress */}
              <Grid xs={12}>
                <FormControl error={!!errors.companyAddress}>
                  <FormLabel>
                    {t("profile.editProfile.personalAddress.addressLine1")}
                    <span className="text-danger-500">*</span>
                  </FormLabel>
                  <Input {...register("companyAddress", { required: true })} />
                  {errors.companyAddress?.message && (
                    <FormHelperText>
                      {errors.companyAddress?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* companyAddress2 */}
              <Grid xs={12}>
                <FormControl error={!!errors.companyAddress2}>
                  <FormLabel>
                    {t("profile.editProfile.personalAddress.addressLine2")}
                  </FormLabel>
                  <Input {...register("companyAddress2")} />
                  {errors.companyAddress2?.message && (
                    <FormHelperText>
                      {errors.companyAddress2?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* companyProvince & companyPostalCode */}
              <Grid xs={12} md={6}>
                <FormControl error={!!errors.companyProvince}>
                  <FormLabel>
                    {t("profile.address.provinceState")}
                    <span className="text-danger-500">*</span>
                  </FormLabel>
                  <Input
                    placeholder={t("profile.address.typeProvinceState")}
                    {...register("companyProvince")}
                  />
                  {errors.companyProvince?.message && (
                    <FormHelperText>
                      {errors.companyProvince?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid xs={12} md={6}>
                <FormControl error={!!errors.companyPostalCode}>
                  <FormLabel>{t("profile.address.postalCode")}</FormLabel>
                  <Input
                    placeholder={t("profile.address.typePostalCode")}
                    {...register("companyPostalCode")}
                  />
                  {errors.companyPostalCode?.message && (
                    <FormHelperText>
                      {errors.companyPostalCode?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>

            {/* set company address same to personal address */}
            <FormControl size="md">
              <Stack
                direction={"row"}
                sx={{
                  alignItems: "center",
                  width: "100%",
                  gap: "12px",
                  py: "8px",
                }}
              >
                <Checkbox
                  checked={isAddressMatch}
                  sx={{
                    alignSelf: "start",
                  }}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    if (isChecked) setIsAddressMatch(true);
                    else {
                      setIsAddressMatch(false);

                      reset((prev) => ({
                        ...prev,
                        ...handleMapAddressToCompanyAddress(business),
                      }));
                    }
                  }}
                />
                <Typography fontSize={14} fontWeight={400}>
                  {t("profile.editProfile.companyAddress.isAddressMatch")}
                </Typography>
              </Stack>
            </FormControl>
          </Stack>

          <Stack
            direction={{ xs: "column-reverse", md: "row" }}
            sx={{
              justifyContent: { md: "flex-end" },
              gap: "16px",
            }}
          >
            <Button
              id="profile-edit-cancel-button"
              variant="linkz-outlined-primary"
              onClick={() => {
                navigate("/client/profile");
              }}
              sx={{
                my: "auto",
                minWidth: { xs: "100%", lg: "68px" },
              }}
            >
              {t("commons.cancelBtn")}
            </Button>

            <Button
              id="profile-edit-submit-desktop"
              sx={{
                my: "auto",
                minWidth: "100px",
              }}
              type="submit"
            >
              {t("commons.saveChangesBtn")}
            </Button>
          </Stack>
        </Stack>
      </form>

      {/* modal */}
      <LoadingModal isLoading={isLoading} />
    </Sheet>
  );
};

const CustomImagePolicy = ({ file }: { file: File | null }) => {
  const [imageSize, _setImageSize] = useState<number[]>([0, 0]);
  const { t } = useTranslation();

  const isLessThenEq2MB = () => {
    return (file?.size || 0) <= 2 * 1024 * 1024;
  };

  const isImage = () => {
    return (file?.type || "").startsWith("image");
  };

  const isRecomendedSize = () => {
    return (file && imageSize[0] === 2000 && imageSize[1] === 2000) || false;
  };

  return (
    <ul>
      <li>
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          <PolicyBullet isActive={isLessThenEq2MB() && isImage()} />
          <Typography
            fontSize={14}
            textColor={
              isLessThenEq2MB() && isImage() ? "primary.400" : "neutral.500"
            }
          >
            {t("imagePolicy.requirement1")}
          </Typography>
        </Stack>
      </li>
      <li>
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          <PolicyBullet isActive={isRecomendedSize()} />
          <Typography
            fontSize={14}
            textColor={isRecomendedSize() ? "primary.400" : "neutral.500"}
          >
            {t("imagePolicy.requirement2")}
          </Typography>
        </Stack>
      </li>
    </ul>
  );
};

export default UpdatePersonalProfile;
